import { Route, Routes } from "react-router-dom";

import ACL_RELATIONSHIPS from "acl-relationships";
import withRoleCheck from "components/hocs/withRoleCheck";
import { useRuvixxFlags } from "providers/RuvixxFlagsProvider";
import CONFIG from "Config";
import NotFoundItem from "./404/NotFoundItem";
import _AgentDashboardTable from "./agent_dashboard/AgentDashboardTableV3";
import _CallsTable from "components/CallsTableV3/CallsTableV3";
import _CampaignDetail from "./campaign/CampaignDetail";
import _CampaignsTable from "./campaign/CampaignsTableV3";
import CampaignTarget from "./campaign/CampaignTarget";
import _CaseDetail from "./case/CaseDetail";
import _CaseMap from "components/CaseMap";
import _CasesTable from "./case/CasesTableV3";
import _Contact from "./contact/Contact";
import _ContactsTable from "./contact/components/contacts_table/ContactsTableV3";
import ContactFormPage from "routes/contact/form/ContactFormPage";
import _CallDashboard from "./dashboard/CallDashboard";
import _DataJobTable from "./data_tab/DataJobTable";
import _DialSessionsTable from "./dial_sessions/components/DialSessionTable/DialSessionTableV3";
import _PowerDialer from "./dial_sessions/PowerDialer";
import _PowerDialerMonitor from "./dial_sessions/PowerDialerMonitor";
import _EmailFooter from "./email/EmailFooter";
import _EmailSignature from "./email/EmailSignature";
import _EmailExemption from "./email/EmailExemption";
import _EmailAttachment from "./email/EmailAttachment";
import _EmailTemplate from "./email/EmailTemplate";
import _Entity from "./entity/Entity";
import _EntitiesTable from "./entity/components/entity_table/EntitiesTableV3";
import SwoogoConnectionPage from "./swoogo/ConnectionPage";
import SwoogoAccountPage from "./swoogo/SwoogoAccountPage";
import SwoogoEventDetail from "./swoogo/EventDetail";
import SwoogoRegistrantDetail from "./swoogo/RegistrantDetail";
import SwoogoSessionDetail from "./swoogo/SessionDetail";

import Login from "./login/Login";
import OAuthLogin from "./oauth-login/OAuthLogin";
import ResetPasswordRedirect from "./login/ResetPasswordRedirect";
import UiSandboxRouter from "./ui_sandbox/UiSandboxRouter";
import _SmtpAccountsTable from "./smtp_accounts/SmtpAccountsTableV3";
import _UserSettings from "./user/User";
import _Settings from "./settings/Settings";
import _RevenueOpportunitiesTable from "components/RevenueOpportunitiesTableV3/RevenueOpportunitiesTableV3";
import RevenueOpportunity from "./revenue_opportunity/RevenueOpportunity";
import RevenueOpportunityFormPage from "routes/revenue_opportunity/form/RevenueOpportunityFormPage";
import _MachinesTable from "./machine/MachinesTableV3";
import { COMPLETE_MACHINES_TABLE_NAME } from "./machine/MachinesTableConstants";
import TableDataPage from "./custom_tables/TableDataPage";
import {
  LeadSourceForm,
  LeadSourceLeadDetail,
  LeadSourceLeads,
  LeadSourceManage,
} from "./leads/LeadSourceRouter";
import _CustomTableRowForm from "./custom_tables/CustomTableRowForm";
import CustomTableRowDetail from "./custom_tables/CustomTableRowDetail";
import ImportWizard from "./custom_tables/import/ImportWizard";
import { CollapsedSidebarLayout } from "components/layouts/CollapsedSidebarLayout";
import { OpenLayout } from "components/layouts/OpenLayout";
import { PaddedLayout } from "components/layouts/PaddedLayout";
import { ProtectedLayout } from "components/layouts/ProtectedLayout";
import _SchemaEditor from "routes/custom_tables/settings/SchemaEditor";
import _CustomTablesAdmin from "routes/custom_tables/settings/CustomTablesAdmin";
import TagForm from "routes/tags/TagForm";
import NoteTemplateForm from "routes/note_template/NoteTemplateForm";
import EmailUrl from "routes/email_urls/EmailUrl";
import UserForm from "routes/admin/components/UserForm";
import RoleForm from "routes/admin/components/RoleForm";
import FeatureFlagsPage from "routes/feature_flags/FeatureFlagsPage";
import FeatureFlagForm from "routes/feature_flags/FeatureFlagForm";
import _TagManagementTable from "routes/tags/TagManagementTableV3";
import MetaDatasetsPage from "routes/meta_datasets/MetaDatasetsPage";
import MetaDatasetForm from "routes/meta_datasets/MetaDatasetForm";
import SettingsLayout from "./settings/SettingsLayout";
import _CustomFieldConfig from "./custom_field/CustomFieldConfig";

const CustomFieldConfig = withRoleCheck(
  _CustomFieldConfig,
  [ACL_RELATIONSHIPS.customFieldConfig.readTable],
  { showMsg: true }
);

const CampaignsTable = withRoleCheck(
  _CampaignsTable,
  [
    ACL_RELATIONSHIPS.campaign.read,
    ACL_RELATIONSHIPS.campaigns.read,
    ACL_RELATIONSHIPS.region.read,
  ],
  { showMsg: true }
);

const CampaignDetail = withRoleCheck(
  _CampaignDetail,
  [
    ACL_RELATIONSHIPS.campaign.read,
    ACL_RELATIONSHIPS.campaigns.read,
    ACL_RELATIONSHIPS.region.read,
    ACL_RELATIONSHIPS.tag.read,
    ACL_RELATIONSHIPS.campaignActivity.read,
    ACL_RELATIONSHIPS.user.read,
    ACL_RELATIONSHIPS.emailTemplates.read,
    ACL_RELATIONSHIPS.campaignTarget.read,
  ],
  { showMsg: true }
);

const CasesTable = withRoleCheck(_CasesTable, [ACL_RELATIONSHIPS.case.read], {
  showMsg: true,
});

const CaseDetail = withRoleCheck(_CaseDetail, [ACL_RELATIONSHIPS.case.read], {
  showMsg: true,
});

const CaseMap = withRoleCheck(
  _CaseMap,
  [ACL_RELATIONSHIPS.case.read, ACL_RELATIONSHIPS.machine.read],
  { showMsg: true }
);

const EmailTemplate = withRoleCheck(
  _EmailTemplate,
  [ACL_RELATIONSHIPS.emailTemplates.read],
  { showMsg: true }
);

const EmailFooter = withRoleCheck(
  _EmailFooter,
  [ACL_RELATIONSHIPS.emailFooters.read],
  { showMsg: true }
);

const EmailSignature = withRoleCheck(
  _EmailSignature,
  [ACL_RELATIONSHIPS.emailSignatures.read],
  { showMsg: true }
);

const EmailExemption = withRoleCheck(
  _EmailExemption,
  [ACL_RELATIONSHIPS.emailExemption.read],
  { showMsg: true }
);

const EmailAttachment = withRoleCheck(
  _EmailAttachment,
  [ACL_RELATIONSHIPS.emailAttachments.read],
  { showMsg: true }
);

const EntitiesTable = withRoleCheck(
  _EntitiesTable,
  [
    ACL_RELATIONSHIPS.user.read,
    ACL_RELATIONSHIPS.tag.read,
    ACL_RELATIONSHIPS.entityCustomFields.read,
    ACL_RELATIONSHIPS.entity.read,
  ],
  { showMsg: true }
);

const Entity = withRoleCheck(
  _Entity,
  [
    ACL_RELATIONSHIPS.entity.read,
    ACL_RELATIONSHIPS.tag.read,
    ACL_RELATIONSHIPS.entityCampaigns.read,
    ACL_RELATIONSHIPS.campaigns.read,
    ACL_RELATIONSHIPS.auditLog.read,
    ACL_RELATIONSHIPS.user.read,
    ACL_RELATIONSHIPS.twilioToken.read,
    ACL_RELATIONSHIPS.disposition.read,
  ],
  { showMsg: true }
);
const UserSettings = withRoleCheck(
  _UserSettings,
  [ACL_RELATIONSHIPS.userSettings.read],
  { showMsg: true }
);
const Settings = withRoleCheck(
  _Settings,
  [
    ACL_RELATIONSHIPS.adminUser.read,
    ACL_RELATIONSHIPS.adminRole.read,
    ACL_RELATIONSHIPS.adminGroup.create,
    ACL_RELATIONSHIPS.disposition.read,
    ACL_RELATIONSHIPS.voicemail.read,
    ACL_RELATIONSHIPS.campaignStatusPreset.readAsTable,
    ACL_RELATIONSHIPS.region.read,
    ACL_RELATIONSHIPS.revenueOpportunityStatus.read,
    ACL_RELATIONSHIPS.constantContactAccounts.read,
    ACL_RELATIONSHIPS.constantContactCampaigns.read,
    ACL_RELATIONSHIPS.customFieldGroup.read,
    ACL_RELATIONSHIPS.customFieldConfig.readTable,
    ACL_RELATIONSHIPS.customTable.readSchema,
    ACL_RELATIONSHIPS.product.read,
    ACL_RELATIONSHIPS.tag.readTable,
    ACL_RELATIONSHIPS.tagCategory.readTable,
  ],
  { showMsg: true, partialMatch: true }
);
const ContactsTable = withRoleCheck(
  _ContactsTable,
  [
    ACL_RELATIONSHIPS.user.read,
    ACL_RELATIONSHIPS.tag.read,
    ACL_RELATIONSHIPS.contactCustomFields.read,
    ACL_RELATIONSHIPS.contact.read,
  ],
  { showMsg: true }
);
const Contact = withRoleCheck(
  _Contact,
  [
    ACL_RELATIONSHIPS.contact.read,
    ACL_RELATIONSHIPS.contactCampaigns.read,
    ACL_RELATIONSHIPS.tag.read,
    ACL_RELATIONSHIPS.entity.read,
    ACL_RELATIONSHIPS.campaigns.read,
    ACL_RELATIONSHIPS.auditLog.read,
    ACL_RELATIONSHIPS.user.read,
    ACL_RELATIONSHIPS.twilioToken.read,
    ACL_RELATIONSHIPS.disposition.read,
  ],
  { showMsg: true }
);
const DialSessions = withRoleCheck(
  _DialSessionsTable,
  [ACL_RELATIONSHIPS.dialSession.read],
  { showMsg: true }
);

const CallsTable = withRoleCheck(
  _CallsTable,
  [ACL_RELATIONSHIPS.campaignTargetCall.readTable],
  {
    showMsg: true,
  }
);

const PowerDialer = withRoleCheck(
  _PowerDialer,
  [
    ACL_RELATIONSHIPS.dialSession.read,
    ACL_RELATIONSHIPS.callQueueNextContact.read,
    ACL_RELATIONSHIPS.callQueueAddContact.create,
    ACL_RELATIONSHIPS.disposition.read,
    ACL_RELATIONSHIPS.emailUrlForms.read,
    ACL_RELATIONSHIPS.twilioToken.read,
    ACL_RELATIONSHIPS.tag.read,
    ACL_RELATIONSHIPS.user.read,
    ACL_RELATIONSHIPS.campaignStatuses.read,
    ACL_RELATIONSHIPS.twilioDisposition.create,
    ACL_RELATIONSHIPS.twilioAddToCall.create,
    ACL_RELATIONSHIPS.twilioRemoveFromCall.create,
    ACL_RELATIONSHIPS.twilioUpdateParticipant.create,
  ],
  { showMsg: true }
);

const PowerDialerMonitor = withRoleCheck(
  _PowerDialerMonitor,
  [
    ACL_RELATIONSHIPS.dialSession.read,
    ACL_RELATIONSHIPS.callQueueNextContact.read,
    ACL_RELATIONSHIPS.callQueueAddContact.create,
    ACL_RELATIONSHIPS.disposition.read,
    ACL_RELATIONSHIPS.emailUrlForms.read,
    ACL_RELATIONSHIPS.twilioToken.read,
    ACL_RELATIONSHIPS.tag.read,
    ACL_RELATIONSHIPS.user.read,
    ACL_RELATIONSHIPS.campaignStatuses.read,
    ACL_RELATIONSHIPS.callQueueItemsWithAgentAssigned.read,
    ACL_RELATIONSHIPS.twilioDisposition.create,
    ACL_RELATIONSHIPS.twilioAddToCall.create,
    ACL_RELATIONSHIPS.twilioRemoveFromCall.create,
    ACL_RELATIONSHIPS.twilioUpdateParticipant.create,
  ],
  { showMsg: true }
);

const AgentDashboard = withRoleCheck(
  _AgentDashboardTable,
  [
    ACL_RELATIONSHIPS.dialSession.read,
    ACL_RELATIONSHIPS.callQueueNextContact.read,
    ACL_RELATIONSHIPS.disposition.read,
    ACL_RELATIONSHIPS.emailUrlForms.read,
    ACL_RELATIONSHIPS.twilioToken.read,
    ACL_RELATIONSHIPS.tag.read,
    ACL_RELATIONSHIPS.user.read,
    ACL_RELATIONSHIPS.campaignStatuses.read,
    ACL_RELATIONSHIPS.callQueueItemsWithAgentAssigned.read,
    ACL_RELATIONSHIPS.twilioDisposition.create,
    ACL_RELATIONSHIPS.twilioAddToCall.create,
    ACL_RELATIONSHIPS.twilioRemoveFromCall.create,
    ACL_RELATIONSHIPS.twilioUpdateParticipant.create,
  ],
  { showMsg: true }
);

const CallDashboard = withRoleCheck(
  _CallDashboard,
  [
    ACL_RELATIONSHIPS.campaigns.read,
    ACL_RELATIONSHIPS.dialSession.read,
    ACL_RELATIONSHIPS.disposition.read,
    ACL_RELATIONSHIPS.user.read,
    ACL_RELATIONSHIPS.campaignTargetCall.read,
  ],
  { showMsg: true }
);

const SmtpAccountsTable = withRoleCheck(
  _SmtpAccountsTable,
  [
    ACL_RELATIONSHIPS.emailConfigSmtp.read,
    ACL_RELATIONSHIPS.emailConfigSmtpTable.read,
  ],
  { showMsg: true }
);

const DataJobTable = withRoleCheck(
  _DataJobTable,
  [ACL_RELATIONSHIPS.dataJob.read],
  { showMsg: true }
);

const RevenueOpportunitiesTable = withRoleCheck(
  _RevenueOpportunitiesTable,
  [ACL_RELATIONSHIPS.revenueOpportunity.readTable],
  { showMsg: true }
);

const MachinesTable = withRoleCheck(
  _MachinesTable,
  [ACL_RELATIONSHIPS.machine.read],
  { showMsg: true }
);

const CustomTableRowNew = () => <_CustomTableRowForm mode="create" />;

const CustomTableRowEdit = () => <_CustomTableRowForm mode="edit" />;

const SchemaEditor = withRoleCheck(
  _SchemaEditor,
  [ACL_RELATIONSHIPS.customTable.editSchema],
  { showMsg: true }
);

const CustomTablesAdmin = withRoleCheck(
  _CustomTablesAdmin,
  [ACL_RELATIONSHIPS.customTable.readSchema],
  { showMsg: true }
);

const TagManagementTable = withRoleCheck(
  _TagManagementTable,
  [ACL_RELATIONSHIPS.tag.readTable],
  { showMsg: true }
);

const Router = () => {
  const {
    enable_ruvixx_flags_sys_settings,
    enable_leads_feature,
    enable_meta_account,
    enable_swoogo_integration,
    enable_ui_sandbox,
  } = useRuvixxFlags();
  const isMetaAccountEnabled = enable_leads_feature && enable_meta_account;

  return (
    <Routes>
      <Route element={<OpenLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/oauth-login" element={<OAuthLogin />} />
        <Route path="/reset_password" element={<ResetPasswordRedirect />} />
      </Route>

      <Route element={<ProtectedLayout />}>
        <Route element={<PaddedLayout />}>
          <Route path="/" element={<CampaignsTable />} />
          <Route path="agent_dashboard" element={<AgentDashboard />} />
          <Route path="calls" element={<CallsTable />} />
          {CONFIG.IS_PHONE_ENABLED ? (
            <Route path="dial_sessions" element={<DialSessions />} />
          ) : null}
          <Route path="campaigns" element={<CampaignsTable />} />
          <Route path="cases" element={<CasesTable />} />
          <Route path="contacts" element={<ContactsTable />} />
          <Route path="custom_tables/import" element={<ImportWizard />} />
          <Route
            path="custom_tables/:slug/new"
            element={<CustomTableRowNew />}
          />
          <Route
            path="custom_tables/:slug/:rowId/edit"
            element={<CustomTableRowEdit />}
          />
          <Route path="custom_tables/:slug" element={<TableDataPage />} />
          <Route path="entities" element={<EntitiesTable />} />
          <Route
            path="machines"
            element={<MachinesTable tableName={COMPLETE_MACHINES_TABLE_NAME} />}
          />
          <Route
            path="revenue_opportunities"
            element={<RevenueOpportunitiesTable />}
          />
          <Route path="profile" element={<UserSettings />} />
          <Route path="smtp_accounts" element={<SmtpAccountsTable />} />
          {enable_swoogo_integration && (
            <Route path="swoogo/:tab?" element={<SwoogoAccountPage />} />
          )}
        </Route>

        {/* Outside PaddedLayout: the form pages lay out their own header bar
            and scrolling body, like a record page. Ranked above
            revenue_opportunities/:id/:tab? and contacts/:id/:tab? because a
            static segment always wins. */}
        <Route
          path="revenue_opportunities/new"
          element={<RevenueOpportunityFormPage />}
        />
        <Route
          path="revenue_opportunities/:id/edit"
          element={<RevenueOpportunityFormPage />}
        />
        <Route path="contacts/new" element={<ContactFormPage />} />
        <Route path="contacts/:id/edit" element={<ContactFormPage />} />

        <Route element={<CollapsedSidebarLayout />}>
          <Route path="data" element={<DataJobTable />} />
          {enable_swoogo_integration && (
            // Ranked above swoogo/:tab? because a static segment always wins.
            <>
              <Route
                path="swoogo/events/:id/:tab?"
                element={<SwoogoEventDetail />}
              />
              <Route
                path="swoogo/registrants/:id/:tab?"
                element={<SwoogoRegistrantDetail />}
              />
              <Route
                path="swoogo/sessions/:id/:tab?"
                element={<SwoogoSessionDetail />}
              />
            </>
          )}

          {/* Ranked above cases/:id/:tab? — a static segment always wins, so
              "map" is never matched as a tab. */}
          <Route path="cases/:id/map" element={<CaseMap />} />
          {isMetaAccountEnabled && (
            <Route
              path="leads/:service/:accountId/lead/:leadId/:tab?"
              element={<LeadSourceLeadDetail />}
            />
          )}
          {isMetaAccountEnabled && (
            <Route
              path="leads/:service/:accountId/:tab?"
              element={<LeadSourceLeads />}
            />
          )}
          <Route path="cases/:id/:tab?" element={<CaseDetail />} />
          <Route path="contacts/:id/:tab?" element={<Contact />} />
          <Route path="entities/:id/:tab?" element={<Entity />} />
          <Route
            path="revenue_opportunities/:id/:tab?"
            element={<RevenueOpportunity />}
          />

          <Route path="campaigns">
            <Route path=":id/:tab?" element={<CampaignDetail />} />
            <Route
              path=":id/target/:entity_id/:tab?"
              element={<CampaignTarget />}
            />
          </Route>

          <Route path="custom_tables">
            <Route path=":slug/:rowId" element={<CustomTableRowDetail />} />
            <Route
              path=":slug/:rowId/:tab"
              element={<CustomTableRowDetail />}
            />
          </Route>

          <Route path="settings" element={<SettingsLayout />}>
            <Route index element={<Settings />} />
            <Route path="tags" element={<TagManagementTable />} />
            <Route path="tags/new" element={<TagForm mode="create" />} />
            <Route path="tags/:id/edit" element={<TagForm mode="edit" />} />
            {enable_ruvixx_flags_sys_settings && (
              <>
                <Route path="feature_flags" element={<FeatureFlagsPage />} />
                <Route
                  path="feature_flags/new"
                  element={<FeatureFlagForm mode="create" />}
                />
                <Route
                  path="feature_flags/:id/edit"
                  element={<FeatureFlagForm mode="edit" />}
                />
              </>
            )}
            <Route path="users/new" element={<UserForm mode="create" />} />
            <Route path="users/:id/edit" element={<UserForm mode="edit" />} />
            <Route path="roles/new" element={<RoleForm mode="create" />} />
            <Route path="roles/:id/edit" element={<RoleForm mode="edit" />} />
            <Route
              path="note_templates/new"
              element={<NoteTemplateForm mode="create" />}
            />
            <Route
              path="note_templates/:id/edit"
              element={<NoteTemplateForm mode="edit" />}
            />
            <Route path="custom_tables" element={<CustomTablesAdmin />} />
            <Route path="custom_tables/new" element={<SchemaEditor />} />
            <Route path="custom_tables/:slug/edit" element={<SchemaEditor />} />
            {enable_swoogo_integration && (
              <Route
                path="integrations/swoogo"
                element={<SwoogoConnectionPage />}
              />
            )}
            {isMetaAccountEnabled && (
              <>
                <Route
                  path="integrations/:service"
                  element={<LeadSourceManage />}
                />
                <Route
                  path="integrations/:service/new"
                  element={<LeadSourceForm mode="create" />}
                />
                <Route
                  path="integrations/:service/:id/edit"
                  element={<LeadSourceForm mode="edit" />}
                />
              </>
            )}
            {enable_leads_feature && (
              <>
                <Route path="meta_datasets" element={<MetaDatasetsPage />} />
                <Route
                  path="meta_datasets/new"
                  element={<MetaDatasetForm mode="create" />}
                />
                <Route
                  path="meta_datasets/:id/edit"
                  element={<MetaDatasetForm mode="edit" />}
                />
              </>
            )}
            <Route path="email_templates/new" element={<EmailTemplate />} />
            <Route
              path="email_templates/:id"
              element={<EmailTemplate mode="edit" />}
            />
            <Route path="email_urls/new" element={<EmailUrl mode="create" />} />
            <Route
              path="email_urls/:id/edit"
              element={<EmailUrl mode="edit" />}
            />
            <Route path="email_footers/new" element={<EmailFooter />} />
            <Route
              path="email_footers/:id"
              element={<EmailFooter mode="edit" />}
            />
            <Route path="email_signatures/new" element={<EmailSignature />} />
            <Route
              path="email_signatures/:id"
              element={<EmailSignature mode="edit" />}
            />
            <Route path="email_exemptions/new" element={<EmailExemption />} />
            <Route
              path="email_exemptions/:id"
              element={<EmailExemption mode="edit" />}
            />
            <Route path="email_attachments/new" element={<EmailAttachment />} />
            <Route
              path="email_attachments/:id"
              element={<EmailAttachment mode="edit" />}
            />
            <Route
              path="custom_field_configs/new"
              element={<CustomFieldConfig />}
            />
            <Route
              path="custom_field_configs/:id"
              element={<CustomFieldConfig mode="edit" />}
            />
            <Route path=":item/*" element={<Settings />} />
          </Route>

          {enable_ui_sandbox && (
            <Route path="ui-sandbox/*" element={<UiSandboxRouter />} />
          )}
        </Route>

        <Route path="call_dashboard">
          <Route index element={<CallDashboard />} />
          <Route path=":tab" element={<CallDashboard />} />
        </Route>
        <Route path="*" element={<NotFoundItem />} />
      </Route>

      <Route element={<ProtectedLayout hideNav />}>
        <Route path="dialer/:id" element={<PowerDialer />} />
        <Route
          path="dialer-monitor/:campaignId/:callId"
          element={<PowerDialerMonitor />}
        />
      </Route>
    </Routes>
  );
};

export { Router };
